'use client';

import { createContext, useContext, useState } from 'react';

import { type ConfigContextType, type Config, setPreferences } from '@openclassrooms/utils/config';
import { setEnvironmentHost } from '@openclassrooms/utils/environment';

export const ConfigContext = createContext<ConfigContextType | null>(null);

export function ConfigProvider({
  children,
  config,
  host,
}: Readonly<{
  children: React.ReactNode;
  config: Config;
  host?: string;
}>) {
  /**
   * Using useState is kind of a hack to initialize immediately the client modules
   * with data coming from the server.
   * Initialize client configuration to use like this:
   * @example
   * import { getConfig } from '@openclassrooms/utils/config';
   * const algolia = getConfig('algolia');
   */
  useState(() => {
    setPreferences(config);
    if (host) {
      setEnvironmentHost(host);
    }
  });

  return (
    /**
     * Allow usage of useConfig hook
     * @example
     * const { config, getConfig } = useConfig();
     * const algolia = getConfig('algolia');
     * // OR
     * const algolia = config.algolia;
     */
    <ConfigContext.Provider
      value={{
        config,
        getConfig(a) {
          return config[a];
        },
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);

  if (context == null) {
    throw new Error('useConfig hook was used without ConfigProvider.');
  }

  return context;
};
